'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

type BannerItem = {
  text: string;
  linkUrl?: string | null;
  linkLabel?: string | null;
};

type PropertyBannerCarouselProps = {
  items: BannerItem[];
  intervalMs?: number | null;
};

export default function PropertyBannerCarousel({
  items,
  intervalMs,
}: PropertyBannerCarouselProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [paused, setPaused] = useState(false);
  const normalizedItems = useMemo(
    () =>
      items
        .map((item) => ({
          text: item.text?.trim() ?? '',
          linkUrl: item.linkUrl?.trim() || null,
          linkLabel: item.linkLabel?.trim() || null,
        }))
        .filter((item) => item.text),
    [items]
  );
  const resolvedInterval = Number.isFinite(intervalMs) && intervalMs && intervalMs > 0 ? intervalMs : 6000;

  useEffect(() => {
    if (normalizedItems.length <= 1 || paused) return;
    const id = window.setInterval(() => {
      const container = containerRef.current;
      if (!container) return;
      const width = container.clientWidth;
      if (width <= 0) return;
      const currentIndex = Math.round(container.scrollLeft / width);
      const nextIndex = currentIndex >= normalizedItems.length - 1 ? 0 : currentIndex + 1;
      container.scrollTo({ left: nextIndex * width, behavior: 'smooth' });
    }, resolvedInterval);
    return () => window.clearInterval(id);
  }, [normalizedItems.length, paused, resolvedInterval]);

  if (normalizedItems.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-[hsl(var(--ghost))]/90 text-[hsl(var(--ghost-foreground))] border-b border-border">
      <div className="mx-auto max-w-6xl px-6 py-3">
        <div
          ref={containerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth"
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          {normalizedItems.map((item, index) => (
            <div
              key={`${item.text}-${index}`}
              className="min-w-full snap-start rounded-xl border border-border bg-background/80 px-4 py-3 shadow-sm backdrop-blur"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold">{item.text}</div>
                {item.linkUrl && item.linkLabel && (
                  <a
                    href={item.linkUrl}
                    className="text-xs font-semibold uppercase tracking-wide text-primary hover:opacity-80"
                  >
                    {item.linkLabel}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
